import React from 'react';
import { useIntl } from 'react-intl';
import {
    CardTitle,
    Row,
    Col,
    Table,
    Label
} from 'reactstrap';
import { enumLedType } from '../../../_metronic/helpers';

// This component is designed to be rendered by react-to-print
// It receives the necessary data as props from VerandaCalculator
const VerandaCalculatorPrint = React.forwardRef((props, ref) => {
    const { formData, calculatedMaterials, companyName, logoUrl } = props;
    const intl = useIntl();

    // Basic print styles - including reorganization styles
    const pageStyle = `
        @page {
            size: A4;
            margin: 20mm;
        }
        @media print {
            body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 0;
                width: 100%;
                -webkit-print-color-adjust: exact; /* Chrome, Safari */
                color-adjust: exact; /* Firefox */
            }
            .print-container {
                width: 100%; /* Use full width within margins */
                padding: 0; /* Padding is handled by @page margin */
                margin: 0 auto;
            }
            .print-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 30px;
                border-bottom: 1px solid #ccc;
                padding-bottom: 15px;
            }
            .print-logo {
                max-height: 60px; /* Adjust size as needed */
                max-width: 200px;
            }
            .print-company-name {
                font-size: 18px;
                font-weight: bold;
                text-align: left;
            }
            .print-header-label {
                font-size: 10px;
                color: #555;
                text-transform: uppercase;
                margin-bottom: 2px;
                display: block;
                font-weight: 600;
            }
            .print-summary-info {
                margin-bottom: 10px;
                padding-bottom: 5px;
            }
             .print-summary-section {
                 margin-bottom: 15px; /* Add space between sections */
                 page-break-inside: avoid; /* Try to keep sections together */
             }
            .print-summary-section h5 { /* Section Titles */
                font-size: 14px;
                font-weight: bold;
                margin-bottom: 10px;
                padding-bottom: 5px;
                border-bottom: 1px solid #eee;
                color: #333;
            }
             .print-summary-subsection h6 { /* Subsection Titles (Dimensions, Material, LED) */
                 font-size: 12px;
                 font-weight: bold;
                 margin-bottom: 8px;
                 color: #555;
                 margin-top: 0;
                 padding-bottom: 3px;
                 border-bottom: 1px dotted #ccc; /* Dotted line for subsections */
             }
            .print-summary-section p, .print-multiline-info { /* Data lines */
                font-size: 12px;
                margin-bottom: 5px;
                line-height: 1.4;
            }
            .print-summary-section strong, .print-multiline-info strong { /* Labels */
                display: inline-block;
                min-width: 110px; /* Adjust width for alignment */
                font-weight: normal;
                color: #333;
                padding-right: 5px;
                flex-shrink: 0;
            }
             .print-summary-section p span.value, .print-multiline-info span { /* Values */
                 font-weight: bold;
                 color: #000;
             }
            .print-multiline-info {
                 display: flex;
                 align-items: flex-start;
            }
            .print-multiline-info span { /* Multiline values */
                 white-space: pre-line;
            }

            .results-card-body {
                padding: 0 !important;
            }
            table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 20px;
                font-size: 11px;
            }
            th, td {
                padding: 6px 8px;
                text-align: left;
                border: 1px solid #ddd;
            }
            th {
                background-color: #f2f2f2 !important;
                font-weight: bold;
            }
            .editable-table-input {
                display: none;
            }
            td span.print-value {
                display: inline !important;
            }
            .signature-area {
                margin-top: 40px;
                text-align: right;
                page-break-inside: avoid;
            }
            .signature-line {
                border-top: 1px solid #000;
                width: 250px;
                display: inline-block;
                margin-top: 40px;
                margin-left: 10px;
            }
            .signature-label {
                font-size: 12px;
                display: inline-block;
                vertical-align: bottom;
                margin-bottom: 1px;
            }
            .no-print {
                display: none !important;
            }
        }
    `;

    // Helper to render form data safely, prioritizing labels
    const renderFormData = (key, labelId) => {
        const value = formData?.[key];
        // Render only if value exists and is not empty string or null/undefined
        if (value !== '' && value !== null && value !== undefined) {
            // Prioritize using the label key if it exists (e.g., 'roofTypeLabel' for 'roofType')
            const displayValue = formData?.[`${key}Label`] ?? String(value);
            // Wrap the value in a span for potential styling
            return <p><strong>{intl.formatMessage({ id: labelId })}:</strong> <span className="value">{displayValue}</span></p>;
        }
        return null;
    };

    // Helper to render multiline text safely
    const renderMultilineData = (key, labelId) => {
        const value = formData?.[key];
        if (value) {
            return (
                <div className="print-multiline-info">
                    <strong>{intl.formatMessage({ id: labelId })}:</strong>
                    <span style={{ whiteSpace: 'pre-line' }}>{value}</span>
                </div>
            );
        }
        return null;
    };

    return (
        <div ref={ref} className="print-container">
            <style>{pageStyle}</style>
            {/* --- Print Header --- */}
            <div className="print-header">
                {logoUrl && <img src={logoUrl} alt={`Logo`} className="print-logo" />}
                <div style={{ display: 'flex', gap: '40px', alignItems: 'flex-start' }}>
                    {formData?.customerInfo && (
                        <div>
                            <span className="print-header-label">
                                {intl.formatMessage({ id: 'PRINT.CLIENT' })}
                            </span>
                            <div style={{ fontSize: '18px', textAlign: 'left', whiteSpace: 'pre-line', fontWeight: 'bold' }}>
                                {formData.customerInfo}
                            </div>
                        </div>
                    )}
                    {formData?.referenceNumber && (
                        <div>
                            <span className="print-header-label">
                                {intl.formatMessage({ id: 'PRINT.REFERENCE' })}
                            </span>
                            <h2 className="print-company-name" style={{ margin: 0 }}>
                                {formData.referenceNumber}
                            </h2>
                        </div>
                    )}
                </div>
            </div>

            {/* --- Print Summary Info --- */}
            {(calculatedMaterials && calculatedMaterials.length > 0 && formData) && (
                <div className="print-summary-info">
                    <Row>
                        {/* Specifications Section (Combined Dimensions, Material, LED) */}
                        <Col xs={12} className="print-summary-section">
                            <Row>
                                {/* Dimensions */}
                                <Col xs={3} className="print-summary-subsection">
                                    <h6>{intl.formatMessage({ id: 'VERANDA_CALCULATOR.DIMENSIONS_SECTION' })}</h6>
                                    {renderFormData('width', 'CHECKOUT.INPUT.WIDTH')}
                                    {renderFormData('depth', 'CHECKOUT.INPUT.DEPTH')}
                                </Col>
                                {/* Material */}
                                <Col xs={5} className="print-summary-subsection">
                                    <h6>{intl.formatMessage({ id: 'VERANDA_CALCULATOR.MATERIAL' })}</h6>
                                    {renderFormData('model', 'CHECKOUT.INPUT.MODELTYPE')}
                                    {renderFormData('frameColor', 'CHECKOUT.INPUT.FRAMECOLOR')}
                                    {renderFormData('roofType', 'CHECKOUT.INPUT.ROOFTYPE')}
                                    {renderFormData('materialColorType', 'CHECKOUT.INPUT.MATERIALCOLOR')}
                                    {renderFormData('maxDepth', 'VERANDA_CALCULATOR.MAX_DEPTH')}
                                </Col>
                                {/* LED */}
                                <Col xs={4} className="print-summary-subsection">
                                    <h6>{intl.formatMessage({ id: 'VERANDA_CALCULATOR.LED' })}</h6>
                                    {renderFormData('nrOfLedPerLigger', 'CHECKOUT.INPUT.NROFLEDPERCOLUMN')}
                                    {Number(formData?.nrOfLedPerLigger) !== 0 && renderFormData('ledType', 'CHECKOUT.INPUT.LEDTYPE')}
                                    {Number(formData?.nrOfLedPerLigger) !== 0 && Number(formData?.ledType) !== enumLedType.Alleen_Gat && renderFormData('ledColor', 'CHECKOUT.INPUT.LEDCOLOR')}
                                </Col>
                            </Row>
                        </Col>

                        <hr />

                        {/* Customer Info Section */}
                        <Col xs={12} className="print-summary-section">
                            <Row>
                                <Col xs={12}>
                                    {renderMultilineData('notes', 'VERANDA_CALCULATOR.NOTES')}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            )}

            {/* --- Results Table --- */}
            {(calculatedMaterials && calculatedMaterials.length > 0) && (
                <div className="results-card">
                    {/* <div className='results-card-header'>
                        <CardTitle tag="h3">{intl.formatMessage({ id: 'VERANDA_CALCULATOR.RESULTS_SECTION_TITLE' })}</CardTitle>
                    </div> */}
                    <div className='results-card-body'>
                        <Table responsive striped bordered hover size="sm">
                            <thead>
                                <tr>
                                    <th>{intl.formatMessage({ id: 'VERANDA_CALCULATOR.TABLE.MATERIAL' })}</th>
                                    <th>{intl.formatMessage({ id: 'VERANDA_CALCULATOR.TABLE.SIZE' })}</th>
                                    <th>{intl.formatMessage({ id: 'VERANDA_CALCULATOR.TABLE.COUNT' })}</th>
                                    <th>{intl.formatMessage({ id: 'VERANDA_CALCULATOR.TABLE.DONE' })}</th>
                                    <th>{intl.formatMessage({ id: 'VERANDA_CALCULATOR.TABLE.DELIVERED' })}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {calculatedMaterials.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.productName}</td>
                                        <td>{item.size}</td>
                                        <td>{item.quantity}</td>
                                        <td>{''}</td>
                                        <td>{''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            )}

            {/* --- Signature Area --- */}
            <div className="signature-area">
                <Label htmlFor="signature" className="signature-label">
                    {intl.formatMessage({ id: 'VERANDA_CALCULATOR.SIGNATURE' })}:
                </Label>
                <div className="signature-line">
                    {/* Placeholder for the actual signature line */}
                </div>
            </div>
        </div>
    );
});

export default VerandaCalculatorPrint;
